import { errorResponseSchema } from '../errors.js';
import { buildPath } from './endpoint.js';
/**
 * The one error type the dashboard and the widget branch on
 * (`patterns/frontend-conventions.md`, 1). It carries the stable `code` from
 * the error envelope (`patterns/error-handling.md`, 1).
 */
export class ApiError extends Error {
    code;
    status;
    reason;
    fields;
    requestId;
    /**
     * The `Retry-After` header in seconds, sent with `RATE_LIMITED`
     * (`patterns/error-handling.md`, 2), or `null` when there was none.
     */
    retryAfterSeconds;
    constructor(init) {
        super(init.message);
        this.name = 'ApiError';
        this.code = init.code;
        this.status = init.status;
        this.reason = init.reason ?? null;
        this.fields = init.fields ?? null;
        this.requestId = init.requestId ?? null;
        this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    }
}
/**
 * The request never reached the API: no response at all (offline, DNS, a
 * dropped connection). Screens tell this apart from a server error, because
 * the two say different things (`ux.md`, States: "Failed, no response" versus
 * "Failed, server error").
 */
export class NetworkError extends Error {
    constructor(options = {}) {
        super('Groundly could not be reached.', options);
        this.name = 'NetworkError';
    }
}
function toQueryString(query) {
    if (query === undefined || query === null)
        return '';
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null)
            continue;
        if (Array.isArray(value)) {
            for (const item of value)
                search.append(key, String(item));
        }
        else {
            search.append(key, String(value));
        }
    }
    const encoded = search.toString();
    return encoded ? `?${encoded}` : '';
}
/**
 * The only way the dashboard and the widget call the API
 * (`patterns/api-conventions.md`, 2): no hand-written URLs, no bare `fetch`.
 * The response is parsed with the endpoint's own schema, so a client that has
 * drifted from the contract fails here rather than rendering wrong data.
 */
export async function callApi(endpoint, input = {}, options = {}) {
    const doFetch = options.fetchImpl ?? globalThis.fetch;
    const params = endpoint.params.parse(input.params ?? {});
    const query = endpoint.query.parse(input.query ?? {});
    const hasBody = endpoint.method !== 'GET';
    const body = hasBody ? endpoint.body.parse(input.body ?? {}) : undefined;
    const sendBody = hasBody && body !== undefined && Object.keys(body).length > 0;
    const url = `${options.baseUrl ?? ''}${buildPath(endpoint.path, params)}${toQueryString(query)}`;
    let response;
    try {
        response = await doFetch(url, {
            method: endpoint.method,
            headers: {
                Accept: 'application/json',
                ...(sendBody ? { 'Content-Type': 'application/json' } : {}),
                ...options.headers,
            },
            ...(sendBody ? { body: JSON.stringify(body) } : {}),
            ...(options.signal ? { signal: options.signal } : {}),
        });
    }
    catch (cause) {
        // An aborted request is the caller's own doing, not a failure to reach
        // Groundly, so it is passed through untouched.
        if (cause instanceof Error && cause.name === 'AbortError')
            throw cause;
        throw new NetworkError({ cause });
    }
    if (!response.ok) {
        throw await toApiError(response);
    }
    if (response.status === 204) {
        return endpoint.response.parse(undefined);
    }
    const payload = await response.json();
    return endpoint.response.parse(payload);
}
/**
 * Groundly sends `Retry-After` as whole seconds. Anything else (absent, an HTTP
 * date, not a positive number) gives `null`, and the screen says to try again
 * later instead of naming a wait.
 */
export function parseRetryAfterSeconds(header) {
    if (header === null || !/^\d+$/.test(header.trim()))
        return null;
    const seconds = Number(header.trim());
    return seconds > 0 ? seconds : null;
}
async function toApiError(response) {
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        payload = undefined;
    }
    const parsed = errorResponseSchema.safeParse(payload);
    if (parsed.success) {
        const { code, message, reason, fields, requestId } = parsed.data.error;
        return new ApiError({
            code,
            status: response.status,
            message,
            reason,
            fields,
            requestId,
            retryAfterSeconds: parseRetryAfterSeconds(response.headers.get('Retry-After')),
        });
    }
    // A failure that never reached the API — a proxy, a network device or
    // Better Auth's own routes (`patterns/error-handling.md`, 1).
    return new ApiError({
        code: 'INTERNAL',
        status: response.status,
        message: `The request failed with status ${response.status}.`,
    });
}
//# sourceMappingURL=client.js.map